import React, { useState } from 'react';
import Image from '../../../components/AppImage';
import LazyImage from '../../../components/LazyImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ShingleModal = ({ shingle, isOpen, onClose }) => {
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [activeTab, setActiveTab] = useState('overview');

  if (!isOpen || !shingle) return null;

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'specifications', label: 'Specifications' },
    { id: 'installation', label: 'Installation' },
    { id: 'warranty', label: 'Warranty' }
  ];

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-2xl font-bold text-text-primary">{shingle?.name}</h2>
            <p className="text-text-secondary">{shingle?.brand}</p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={24} />
          </Button>
        </div>

        <div className="flex flex-col lg:flex-row max-h-[calc(90vh-80px)]">
          {/* Image Section */}
          <div className="lg:w-1/2 p-6">
            {/* Main Image */}
            <div className="mb-4">
              <LazyImage
                src={shingle?.images?.[activeImageIndex]}
                alt={`${shingle?.name} shingle view ${activeImageIndex + 1}`}
                className="w-full h-64 lg:h-80 object-cover rounded-lg"
              />
            </div>

            {/* Image Thumbnails */}
            <div className="flex gap-2 overflow-x-auto">
              {shingle?.images?.map((image, index) => (
                <button
                  key={index}
                  onClick={() => setActiveImageIndex(index)}
                  className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-colors ${
                    activeImageIndex === index ? 'border-primary' : 'border-border'
                  }`}
                >
                  <LazyImage
                    src={image}
                    alt={`${shingle?.name} thumbnail ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>

            {/* Color Swatches */}
            <div className="mt-4">
              <h4 className="font-medium text-text-primary mb-2">Available Colors</h4>
              <div className="grid grid-cols-4 gap-2">
                {shingle?.colors?.map((color, index) => (
                  <div key={index} className="text-center">
                    <div
                      className="w-12 h-12 rounded-lg border border-border mx-auto mb-1"
                      style={{ backgroundColor: color?.hex }}
                    />
                    <span className="text-xs text-text-secondary">{color?.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Content Section */}
          <div className="lg:w-1/2 flex flex-col">
            {/* Tabs */}
            <div className="flex border-b border-border">
              {tabs?.map(tab => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`px-4 py-3 text-sm font-medium transition-colors ${
                    activeTab === tab?.id
                      ? 'text-primary border-b-2 border-primary' :'text-text-secondary hover:text-text-primary'
                  }`}
                >
                  {tab?.label}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div className="flex-1 p-6 overflow-y-auto">
              {activeTab === 'overview' && (
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-text-primary mb-2">Description</h4>
                    <p className="text-text-secondary">{shingle?.description}</p>
                  </div>

                  <div>
                    <h4 className="font-semibold text-text-primary mb-2">Key Features</h4>
                    <ul className="space-y-1">
                      {shingle?.features?.map((feature, index) => (
                        <li key={index} className="flex items-center gap-2 text-text-secondary">
                          <Icon name="Check" size={16} className="text-success" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <h5 className="font-medium text-text-primary">Warranty</h5>
                      <p className="text-text-secondary">{shingle?.warranty}</p>
                    </div>
                    <div>
                      <h5 className="font-medium text-text-primary">Wind Rating</h5>
                      <p className="text-text-secondary">{shingle?.windRating}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-1">
                      <Icon name="Star" size={16} className="text-warning fill-current" />
                      <span className="font-medium">{shingle?.rating}</span>
                      <span className="text-text-secondary">({shingle?.reviews} reviews)</span>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'specifications' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {Object.entries(shingle?.specifications)?.map(([key, value]) => (
                      <div key={key} className="flex justify-between py-2 border-b border-border">
                        <span className="text-text-secondary capitalize">{key?.replace(/([A-Z])/g, ' $1')}</span>
                        <span className="font-medium text-text-primary">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === 'installation' && (
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-text-primary mb-2">Installation Requirements</h4>
                    <ul className="space-y-2">
                      {shingle?.installationRequirements?.map((req, index) => (
                        <li key={index} className="flex items-start gap-2 text-text-secondary">
                          <Icon name="AlertCircle" size={16} className="text-warning mt-0.5 flex-shrink-0" />
                          {req}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-semibold text-text-primary mb-2">Installation Time</h4>
                    <p className="text-text-secondary">{shingle?.installationTime}</p>
                  </div>
                </div>
              )}

              {activeTab === 'warranty' && (
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-text-primary mb-2">Warranty Coverage</h4>
                    <p className="text-text-secondary mb-4">{shingle?.warrantyDetails}</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-surface p-4 rounded-lg">
                      <h5 className="font-medium text-text-primary mb-1">Material Warranty</h5>
                      <p className="text-text-secondary">{shingle?.materialWarranty}</p>
                    </div>
                    <div className="bg-surface p-4 rounded-lg">
                      <h5 className="font-medium text-text-primary mb-1">Wind Warranty</h5>
                      <p className="text-text-secondary">{shingle?.windWarranty}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="p-6 border-t border-border">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <span className="text-text-secondary">Starting at</span>
                  <p className="text-2xl font-bold text-primary">${shingle?.priceRange?.min}</p>
                  <span className="text-sm text-text-secondary">per sq ft</span>
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  fullWidth
                  onClick={() => window.location.href = '/gallery'}
                >
                  View Gallery
                </Button>
                <Button
                  variant="default"
                  fullWidth
                  onClick={() => window.location.href = '/instant-estimate'}
                >
                  Get Quote
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShingleModal;